import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import ResizeObserver from 'resize-observer-polyfill';
import { defer, finalize, isObservable, Observable, Subject, throttleTime } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export class ResizeObserverHelper {
  private static resizeObserver: ResizeObserver;
  private static subscribers = new WeakMap<Element, Set<Subject<ResizeObserverEntry>>>();

  private resizeEvents$ = new Subject<ResizeObserverEntry>();
  private isObserving = false;

  /**
   * @param element the element to observe for resize changes
   * @param throttleTimeMs throttle time in ms for resize events, it will be called with the trailing edge
   * @param stop$ observable or DestroyRef (⚠️ you need to pass this from injection context if you pass destroy ref!)
   */
  constructor(
    private element: Element,
    private throttleTimeMs: number,
    private stop$: Observable<void> | DestroyRef
  ) {}

  /**
   * Starts observing resize events for the associated element and returns an Observable that emits `ResizeObserverEntry` objects upon resize.
   * It ensures the global `ResizeObserver` instance is created only once and only starts the listener once the returned observable is subscribed to.
   * It is recommended to only call this method once, repeated calls will not start new listeners but return the already existing one.
   */
  public startListen(): Observable<ResizeObserverEntry> {
    if (this.isObserving) {
      return this.observeResizeEvents();
    }
    this.isObserving = true;

    // only create resize observer once when needed first/
    if (!ResizeObserverHelper.resizeObserver) {
      ResizeObserverHelper.resizeObserver = ResizeObserverHelper.createResizeObserver(entries => {
        const entryByTarget = new Map<Element, ResizeObserverEntry>();
        entries.forEach(entry => entryByTarget.set(entry.target, entry));
        entryByTarget.forEach((entry, target) => ResizeObserverHelper.subscribers.get(target)?.forEach(s => s.next(entry)));
      });
    }

    // Added defer to make sure it's only running when there is a subscriber.
    return defer(() => {
      this.startObservingElement();
      return this.observeResizeEvents();
    });
  }

  /**
   * The cleanup function is called inside the class when a proper {@link stop$} Observable<void> | DestroyRef is provided on construct.
   */
  public cleanup(): void {
    const elementSubscribers = ResizeObserverHelper.subscribers.get(this.element);
    elementSubscribers.delete(this.resizeEvents$);
    if (elementSubscribers.size === 0) {
      ResizeObserverHelper.resizeObserver.unobserve(this.element);
      ResizeObserverHelper.subscribers.delete(this.element);
    }
  }

  /**
   * Trigger a new emit. This can be handy if you run into issues e.g., with Safari on iOS where the ResizeObserver does
   * not emit reliably in every case (e.g., autosize textarea size changes are not recognized).
   *
   * ⚠️ Note: if your listener expects any further elements than "target" on the emitted ResizeObserverEntry, this will not work!
   */
  public triggerCheck(): void {
    this.resizeEvents$.next({ target: this.element } as ResizeObserverEntry);
  }

  private startObservingElement(): void {
    if (!ResizeObserverHelper.subscribers.get(this.element)) {
      ResizeObserverHelper.subscribers.set(this.element, new Set());
      ResizeObserverHelper.resizeObserver.observe(this.element);
    }

    ResizeObserverHelper.subscribers.get(this.element).add(this.resizeEvents$);
  }

  private observeResizeEvents(): Observable<ResizeObserverEntry> {
    const stream$ = this.resizeEvents$.pipe(
      throttleTime(this.throttleTimeMs, undefined, { leading: false, trailing: true }),
      finalize(() => this.cleanup())
    );
    if (isObservable(this.stop$)) {
      return stream$.pipe(takeUntil(this.stop$));
    } else {
      return stream$.pipe(takeUntilDestroyed(this.stop$));
    }
  }

  /* separate method to make this mockable in tests */
  private static createResizeObserver(callback: ResizeObserverCallback): ResizeObserver {
    return new ResizeObserver(callback);
  }
}
